import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useKy } from "boot/ky";
import { useAuthUnifiedStore } from "./authUnified.js";
import { usePlatformStore } from "./usePlatformStore.js";
import { baseLogementFields } from "./useLogementsStore.js"; // Importer les champs partagés
import { Notify, Platform } from "quasar";
import { useLogementsStore } from "./useLogementsStore.js"; // Importer le store

/**
 * Store pour gérer les favoris utilisateur
 *
 * Fonctionnalités :
 * - Ajout/suppression de logements en favoris
 * - Liste des favoris de l'utilisateur
 * - Vérification si un logement est en favoris
 * - Gestion des erreurs et états de chargement
 */
export const useFavorisStore = defineStore("favoris", () => {
  // ===== DEPENDENCIES =====
  const { get, post, delete: deleteRequest } = useKy();
  const authStore = useAuthUnifiedStore();
  // ✅ Utiliser le store plateforme initialisé par le boot file (à l'intérieur du store)
  const platformStore = usePlatformStore();

  // ===== STATE =====
  const favoris = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Ajouter une variable pour suivre si le chargement est en cours
  const isLoadingFavoris = ref(false);

  // ===== GETTERS =====
  const favorisCount = computed(() => favoris.value.length);

  const isFavoris = computed(() => (logementId) => {
    return favoris.value.some(
      (favori) =>
        favori.logement?.id === logementId || favori.logement === logementId
    );
  });

  const getFavorisIds = computed(() => {
    return favoris.value.map(
      (favori) => favori.logement?.id || favori.logement
    );
  });

  // ===== ACTIONS =====

  /**
   * Charge la liste des favoris de l'utilisateur connecté
   */
  const loadFavoris = async () => {
    if (!authStore.isAuthenticated) {
      favoris.value = [];
      return;
    }

    // Éviter les chargements multiples simultanés
    if (isLoadingFavoris.value) {
      return;
    }

    isLoadingFavoris.value = true;
    loading.value = true;
    error.value = null;

    const searchParams = {
      fields: `id,${baseLogementFields
        .map((field) => `logement.${field}`)
        .join(",")}`,
      filter: JSON.stringify({
        user_created: "$CURRENT_USER",
      }),
    };

    try {
      const response = await get("items/favoris", {
        searchParams,
      });
      favoris.value = response.data || [];
    } catch (err) {
      error.value = "Impossible de charger les favoris";
      favoris.value = [];
    } finally {
      loading.value = false;
      isLoadingFavoris.value = false;
    }
  };
  /**
   * Ajoute un logement aux favoris
   * @param {string|number} logementId - ID du logement à ajouter
   */
  const ajouterFavori = async (logementId) => {
    if (!authStore.isAuthenticated) {
      throw new Error("Utilisateur non connecté");
    }

    if (isFavoris.value(logementId)) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await post(
        "items/favoris?fields=*,logement.*,logement.photos.directus_files_id,logement.photos.id,logement.type_de_logements.*,logement.liste_commodites.commodites_id.*,logement.liste_commodites.id,logement.liste_equipements.equipements_id.*,logement.liste_equipements.id,logement.ville.*,logement.liste_de_reduction.type_de_reduction_id.id,logement.liste_de_reduction.id,logement.user_created.first_name",
        {
          logement: logementId,
        }
      );
      // Ajouter le nouveau favori à la liste locale
      if (response.data) {
        Notify.create({
          message: "Logement ajouté aux favoris",

          position: "top",

          color: "green-1",
          textColor: "green-10",
          classes: `notify-success shadow-md  animated slower  border-radius  ${
            platformStore.androidVersion >= 12 && Platform.is.android
              ? "notify-margin-top-12"
              : ""
          }`,
          timeout: 5000,
          // Set the progress property to true to display a progress bar.
        });
        favoris.value.push(response.data);
      }

      return response.data;
    } catch (err) {
      error.value = "Impossible d'ajouter aux favoris";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Retire un logement des favoris
   * @param {string|number} logementId - ID du logement à retirer
   */
  const retirerFavori = async (logementId) => {
    if (!authStore.isAuthenticated) {
      throw new Error("Utilisateur non connecté");
    }

    const favori = favoris.value.find(
      (f) => f.logement?.id === logementId || f.logement === logementId
    );
    if (!favori) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      await deleteRequest(`items/favoris/${favori.id}`);

      // Retirer le favori de la liste locale
      const index = favoris.value.findIndex((f) => f.id === favori.id);
      if (index > -1) {
        favoris.value.splice(index, 1);
        Notify.create({
          message: "Logement retiré des favoris",

          position: "top",

          color: "green-1",
          textColor: "green-10",
          classes: `notify-success shadow-md  animated slower  border-radius  ${
            platformStore.androidVersion >= 12 && Platform.is.android
              ? "notify-margin-top-12"
              : ""
          }`,
          timeout: 5000,
          // Set the progress property to true to display a progress bar.
        });
      }
    } catch (err) {
      error.value = "Impossible de retirer des favoris";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Toggle un logement en favoris (ajoute si pas présent, retire si présent)
   * @param {string|number} logementId - ID du logement
   */
  const toggleFavori = async (logementId) => {
    const logementsStore = useLogementsStore();
    const isCurrentlyFavoris = isFavoris.value(logementId);

    try {
      if (isCurrentlyFavoris) {
        await retirerFavori(logementId);
        // Après avoir retiré, il n'est PLUS favori
        logementsStore.updateLogementFavorisStatus(logementId, false);
      } else {
        await ajouterFavori(logementId);
        // Après avoir ajouté, il EST favori
        logementsStore.updateLogementFavorisStatus(logementId, true);
      }
    } catch (error) {
      // En cas d'erreur, restaurer l'état précédent pour éviter un UI incohérent
      logementsStore.updateLogementFavorisStatus(
        logementId,
        isCurrentlyFavoris
      );
    }
  };

  /**
   * Vide tous les favoris de l'utilisateur
   */
  const viderFavoris = async () => {
    if (!authStore.isAuthenticated || favoris.value.length === 0) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      // Supprimer tous les favoris un par un
      const promises = favoris.value.map((favori) =>
        deleteRequest(`items/favoris/${favori.id}`)
      );

      await Promise.all(promises);
      favoris.value = [];
    } catch (err) {
      error.value = "Impossible de vider les favoris";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Nettoie le store (utilisé lors de la déconnexion)
   */
  const clearFavoris = () => {
    favoris.value = [];
    error.value = null;
    loading.value = false;
  };

  return {
    // State
    favoris,
    loading,
    error,

    // Getters
    favorisCount,
    isFavoris,
    getFavorisIds,

    // Actions
    loadFavoris,
    ajouterFavori,
    retirerFavori,
    toggleFavori,
    viderFavoris,
    clearFavoris,
  };
});
